import axios from 'axios';
import formatDateForBackend from '../../utils/formatDateForBackend';
import formatTimeForBackend from '../../utils/formatTimeForBackend';
import { formatEuros, centsInSpan } from '../../utils/euros';
import {
  additionnalDrivers, cdw as localStorageCdw, additionnalKms as localStorageKms, coupon as localStorageCoupon,
} from '../../utils/setLocalStorage'
import { handleErrors } from './quotation/handleErrors'

// We have to make sure the last generate call is used
let lastGenerate


export default {
  namespaced: true,
  state: {
    detail: undefined,
    coupon: '',
    kilometers: NaN,
    cdw: undefined,
    requestingQuotation: false,
    APIErrors: [],
    rentPlusSubscription: undefined,
    firstFetchDone: false,
    hideQuotation: false,
    highlightCdw: false,
    discountLevel: 0,
    additionalDriversIds: [],
    pdfUrl: null,
    pdfValidPromoCode: null,
    isWebCheckoutPossible: false,
  },
  mutations: {
    SET_DETAILED_QUOTATION(state, quot) {
      state.detail = quot;
    },
    SET_COUPON(state, coupon) {
      state.coupon = coupon;
      localStorageCoupon(coupon)
    },
    SET_KM(state, kilometers) {
      state.kilometers = kilometers;
      localStorageKms(kilometers)
    },
    SET_CDW(state, bool) {
      state.cdw = bool;
      localStorageCdw(bool)
    },
    SET_REQUESTING_QUOTATION(state, bool) {
      state.requestingQuotation = bool;
    },
    SET_API_ERRORS(state, errors) {
      state.APIErrors = errors;
    },
    REMOVE_API_ERROR(state, errorCode) {
      state.APIErrors = state.APIErrors.filter((error) => error.code !== errorCode)
    },
    SET_RENT_PLUS_SUBSCRIPTION(state, bool) {
      state.rentPlusSubscription = bool;
    },
    SET_FIRST_FETCH_DONE(state) {
      state.firstFetchDone = true;
    },
    SET_HIDE_QUOTATION(state, bool) {
      state.hideQuotation = bool;
    },
    SET_DISCOUNT_LEVEL(state, val) {
      state.discountLevel = val
    },
    SET_CDW_HIGHLIGHTED(state) {
      state.highlightCdw = true
    },
    SET_ADDITIONAL_DRIVERS_IDS(state, ids) {
      state.additionalDriversIds = ids
      additionnalDrivers(state.additionalDriversIds)
    },
    SET_PDF_URL(state, url) {
      state.pdfUrl = url
    },
    SET_PDF_VALID_PROMO_CODE(state, valid) {
      state.pdfValidPromoCode = valid
    },
    SET_IS_WEBCHECKOUT_POSSIBLE(state, bool) {
      state.isWebCheckoutPossible = bool
    },
  },
  actions: {
    ADD_DRIVER_TO_QUOTATION({ commit, state }, driver) {
      const additionalDriversIds = [...state.additionalDriversIds]
      if (additionalDriversIds.length >= 2) {
        return
      }
      if (additionalDriversIds.findIndex((it) => `${it.id}` === `${driver.id}`) !== -1) {
        return
      }

      additionalDriversIds.push(`${driver.id}`)

      commit('SET_ADDITIONAL_DRIVERS_IDS', additionalDriversIds)
    },
    REMOVE_DRIVER_FROM_QUOTATION({ commit, state }, idToRemove) {
      const additionalDriversIds = state.additionalDriversIds.filter((id) => `${id}` !== `${idToRemove}`)

      commit('SET_ADDITIONAL_DRIVERS_IDS', additionalDriversIds)
    },
    ON_FETCH_START({ dispatch, commit }) {
      commit('SET_REQUESTING_QUOTATION', true);
      dispatch('ui/SET_PAGELOAD_TRUE', undefined, { root: true });
    },
    ON_FETCH_END({ dispatch, commit }) {
      commit('SET_FIRST_FETCH_DONE');
      commit('SET_REQUESTING_QUOTATION', false);
      dispatch('ui/SET_PAGELOAD_FALSE', undefined, { root: true });
    },
    async GET_DETAILED_QUOTATION({
      commit, getters, rootState, dispatch,
    }) {
      const time = new Date()
      let res
      dispatch('ON_FETCH_START');
      commit('SET_API_ERRORS', [])
      // should fetch api and pagload be the same thing ? A topic to discuss...
      try {
        res = (await axios.post(`${rootState.api}/quotations/generate`, getters.quotationFormData))?.data
      } catch (err) {
        console.error(err)
      }
      const { data, errors } = res

      if (lastGenerate > time) {
        return
      }

      lastGenerate = time

      commit('SET_DETAILED_QUOTATION', data.rrs_quotation)
      if (data?.links?.pdf?.url) {
        commit('SET_PDF_URL', data.links.pdf.url)
      }
      /* eslint-disable-next-line camelcase */
      if (data?.valid_promo_code) {
        commit('SET_PDF_VALID_PROMO_CODE', data.valid_promo_code)
      } else {
        commit('SET_PDF_VALID_PROMO_CODE', false)
      }
      commit('SET_IS_WEBCHECKOUT_POSSIBLE', true)
      if (errors) {
        handleErrors({ errors, commit })
      }

      dispatch('ON_FETCH_END');
    },
  },
  getters: {
    additionalDrivers(state, getters, rootState) {
      if (!rootState.drivers.allDrivers) {
        return []
      }
      return rootState.drivers.allDrivers.filter((driver) => state.additionalDriversIds.includes(`${driver.id}`))
    },
    priceFormattedQuotation(state) {
      if (!state.detail) {
        return {};
      }
      return {
        ...state.detail,
        totalSansRemise: centsInSpan(state.detail.totalSansRemise),
        totalttc: centsInSpan(state.detail.totalttc),
        prixUnitKMSUPTTC: formatEuros(state.detail.prixUnitKMSUPTTC),
        remis1ttc: centsInSpan(state.detail.remis1ttc),
        jrsup1ttc: centsInSpan(state.detail.jrsup1ttc),
        fraisAbandon: centsInSpan(state.detail.fraisAbandon),
      };
    },
    pricePerDay(state) {
      if (!state.detail) {
        return;
      }
      return formatEuros(state.detail.totalttc / state.detail.jourfact);
    },
    extraDayPrice(state) {
      if (!state.detail) {
        return;
      }
      if (!parseInt(state.detail.jrsup1, 10)) {
        return 0;
      }
      return formatEuros(state.detail.jrsup1ttc / state.detail.jrsup1);
    },

    quotationFormData(state, getters, rootState, rootGetters) {
      const { pickUp } = rootState.booking;
      const { drop } = rootState.booking;
      const rentplus = !!(!rootGetters['user/isRentPlusUser'] && state.rentPlusSubscription);

      if (
        !pickUp.business
                    || !drop.business
                    || !rootState.vehicles.set.storeVehicle
      ) {
        return;
      }

      const params = {
        departure_business_id: pickUp.business.id,
        departure_date: formatDateForBackend(pickUp.date),
        departure_time: formatTimeForBackend(pickUp.time),
        return_business_id: drop.business.id,
        return_date: formatDateForBackend(drop.date),
        return_time: formatTimeForBackend(drop.time),
        vehicle_id: rootState.vehicles.set.storeVehicle.id,
        kilometers: parseInt(state.kilometers, 10),
        coupon: state.coupon,
        cdw: state.cdw,
        rent_plus_subscription: rentplus,
        rent_plus_discount_level: state.discountLevel,
        second_driver_id: getters.additionalDrivers[0] && getters.additionalDrivers[0].id,
        third_driver_id: getters.additionalDrivers[1] && getters.additionalDrivers[1].id,
        second_driver_date: getters.additionalDrivers[0] && getters.additionalDrivers[0].licence_date,
        third_driver_date: getters.additionalDrivers[1] && getters.additionalDrivers[1].licence_date,
      };

      /* eslint-disable-next-line camelcase */
      if (rootState?.user?.details?.licence_date) {
        params.main_driver_date = rootState.user.details.licence_date;
      }
      return params;
    },
    couldNotGenerateQuotation(state) {
      return state.firstFetchDone && !state.requestingQuotation && state.APIErrors
    },
    userHasNotSelectedCdw(state) {
      return typeof state.cdw === 'undefined'
    },
    isApiErrorsContainsCode(state) {
      return (code) => state.APIErrors.some((errorItem) => errorItem.code === code)
    },
  },
};
